import React, { useState, useEffect } from 'react';
import { 
  Play, 
  CheckCircle, 
  XCircle, 
  Clock, 
  Zap,
  Database,
  Loader
} from 'lucide-react';
import { versionsApi, testCasesApi, testsApi } from '../services/api';
import { AUTVersion, TestCase, TestExecutionBatchResponse } from '../types';
import { useSetting } from '../hooks/useSettings';

interface TestExecutionProps {
  addNotification: (message: string, type: 'success' | 'error' | 'info' | 'warning') => void;
}

const TestExecution: React.FC<TestExecutionProps> = ({ addNotification }) => {
  const [versions, setVersions] = useState<AUTVersion[]>([]);
  const [testCases, setTestCases] = useState<TestCase[]>([]);
  const [loading, setLoading] = useState(true);
  const [executing, setExecuting] = useState(false);
  const [selectedVersion, setSelectedVersion] = useState<number>(0);
  const [selectedTestCases, setSelectedTestCases] = useState<number[]>([]);
  const [runEvaluation, setRunEvaluation] = useState(true);
  const [results, setResults] = useState<TestExecutionBatchResponse | null>(null);

  // Use organization-specific settings
  const defaultBatchSize = useSetting<number>('general.default_batch_size', 10);
  const evaluationTimeout = useSetting<number>('general.evaluation_timeout_seconds', 30);

  useEffect(() => {
    loadData();
    const handler = (e: any) => {
      loadData();
    };
    window.addEventListener('organizationChanged', handler);
    return () => window.removeEventListener('organizationChanged', handler);
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const orgIdStr = localStorage.getItem('selectedOrganizationId');
      const projectIdStr = localStorage.getItem('selectedProjectId');
      const orgId = orgIdStr ? parseInt(orgIdStr) : 1;
      const projectId = projectIdStr ? parseInt(projectIdStr) : undefined;
      const [versionsData, testCasesData] = await Promise.all([
        projectId ? versionsApi.list(orgId, projectId) : Promise.resolve([] as AUTVersion[]),
        projectId ? testCasesApi.list(orgId, projectId) : Promise.resolve([] as TestCase[])
      ]);
      
      setVersions(versionsData);
      setTestCases(testCasesData);
      
      if (versionsData.length > 0) {
        setSelectedVersion(versionsData[0].id);
      }
    } catch (error) {
      addNotification('Failed to load data', 'error');
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleRunTests = async () => {
    if (selectedVersion === 0) {
      addNotification('Please select a version', 'error');
      return;
    }

    if (selectedTestCases.length === 0) {
      addNotification('Please select at least one test case', 'error');
      return;
    }

    try {
      setExecuting(true);
      const orgIdStr = localStorage.getItem('selectedOrganizationId');
      const orgId = orgIdStr ? parseInt(orgIdStr) : 1;
      const result = await testsApi.runBatch(orgId, {
        test_case_ids: selectedTestCases,
        aut_version_id: selectedVersion,
        run_evaluation: runEvaluation,
        organization_id: orgId
      });
      
      setResults(result);
      addNotification(`Tests completed: ${result.successful_tests} successful, ${result.failed_tests} failed`, 'success');
    } catch (error) {
      addNotification('Failed to run tests', 'error');
      console.error('Error running tests:', error);
    } finally {
      setExecuting(false);
    }
  };

  const handleTestCaseToggle = (testCaseId: number) => {
    setSelectedTestCases(prev => 
      prev.includes(testCaseId)
        ? prev.filter(id => id !== testCaseId)
        : [...prev, testCaseId]
    );
  };

  const handleSelectAll = () => {
    setSelectedTestCases(testCases.map(tc => tc.id));
  };

  const handleSelectNone = () => {
    setSelectedTestCases([]);
  };

  const getStatusColor = (score: number) => {
    if (score >= 0.8) return 'text-green-600 bg-green-100';
    if (score >= 0.6) return 'text-yellow-600 bg-yellow-100';
    return 'text-red-600 bg-red-100';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="bg-slate-800 rounded-lg shadow-sm border border-slate-700 p-6">
        <h1 className="text-2xl font-bold text-slate-100">Test Execution</h1>
        <p className="text-slate-300 mt-2">Run tests against your CustomerSupport RAG system</p>
      </div>

      {/* Test Configuration */}
      <div className="bg-slate-800 rounded-lg shadow-sm border border-slate-700 p-6">
        <h3 className="text-lg font-medium text-slate-100 mb-4">Test Configuration</h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Version Selection */}
          <div>
            <label className="block text-sm font-medium text-slate-200 mb-2">
              Version *
            </label>
            <select
              value={selectedVersion}
              onChange={(e) => setSelectedVersion(Number(e.target.value))}
              className="w-full px-3 py-2 border border-slate-600 rounded-md focus:outline-none focus:ring-1 focus:ring-purple-500 focus:border-purple-500"
            >
              <option value={0}>Select a version</option>
              {versions.map((version) => (
                <option key={version.id} value={version.id}>
                  {version.version_name} {version.is_active && '(Active)'}
                </option>
              ))}
            </select>
          </div>

          {/* Evaluation Toggle */}
          <div>
            <label className="block text-sm font-medium text-slate-200 mb-2">
              Run Evaluation
            </label>
            <div className="flex items-center">
              <input
                type="checkbox"
                checked={runEvaluation}
                onChange={(e) => setRunEvaluation(e.target.checked)}
                className="h-4 w-4 text-purple-500 focus:ring-indigo-500 border-slate-600 rounded"
              />
              <label className="ml-2 text-sm text-slate-200">
                Use OpenAI to evaluate response quality
              </label>
            </div>
          </div>
        </div>

        {/* Test Case Selection */}
        <div className="mt-6">
          <div className="flex items-center justify-between mb-4">
            <label className="block text-sm font-medium text-slate-200">
              Select Test Cases ({selectedTestCases.length} selected)
            </label>
            <div className="flex space-x-2">
              <button
                onClick={handleSelectAll}
                className="text-sm text-purple-500 hover:text-indigo-500"
              >
                Select All
              </button>
              <button
                onClick={handleSelectNone}
                className="text-sm text-slate-300 hover:text-slate-400"
              >
                Select None
              </button>
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 max-h-64 overflow-y-auto border border-slate-700 rounded-md p-4">
            {testCases.map((testCase) => (
              <div
                key={testCase.id}
                className={`p-3 border rounded-md cursor-pointer transition-colors ${
                  selectedTestCases.includes(testCase.id)
                    ? 'border-indigo-500 bg-indigo-50'
                    : 'border-slate-700 hover:border-slate-600'
                }`}
                onClick={() => handleTestCaseToggle(testCase.id)}
              >
                <div className="flex items-start space-x-2">
                  <input
                    type="checkbox"
                    checked={selectedTestCases.includes(testCase.id)}
                    onChange={() => handleTestCaseToggle(testCase.id)}
                    className="mt-1 h-4 w-4 text-purple-500 focus:ring-indigo-500 border-slate-600 rounded"
                  />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-slate-100 line-clamp-3">
                      {testCase.question_text}
                    </p>
                    <div className="flex items-center space-x-2 mt-2">
                      <span className={`inline-flex items-center px-2 py-1 text-xs font-semibold rounded-full ${
                        testCase.source_type === 'pdf' ? 'text-blue-600 bg-blue-100' :
                        testCase.source_type === 'webpage' ? 'text-green-600 bg-green-100' :
                        testCase.source_type === 'csv' ? 'text-purple-600 bg-purple-100' :
                        'text-slate-300 bg-slate-600'
                      }`}>
                        {testCase.source_type}
                      </span>
                      {testCase.category && (
                        <span className="text-xs text-slate-400">{testCase.category}</span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Execute Button */}
        <div className="mt-6">
          <button
            onClick={handleRunTests}
            disabled={executing || selectedVersion === 0 || selectedTestCases.length === 0}
            className="w-full inline-flex items-center justify-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {executing ? (
              <>
                <Loader className="animate-spin h-5 w-5 mr-2" />
                Running Tests...
              </>
            ) : (
              <>
                <Play className="h-5 w-5 mr-2" />
                Run Tests ({selectedTestCases.length} test cases)
              </>
            )}
          </button>
        </div>
      </div>

      {/* Test Results */}
      {results && (
        <div className="bg-slate-800 rounded-lg shadow-sm border border-slate-700 p-6">
          <h3 className="text-lg font-medium text-slate-100 mb-4">Test Results</h3>
          
          {/* Summary */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
            <div className="bg-gray-50 rounded-lg p-4 text-center">
              <div className="text-2xl font-bold text-slate-100">{results.total_tests}</div>
              <div className="text-sm text-slate-300">Total Tests</div>
            </div>
            <div className="bg-green-50 rounded-lg p-4 text-center">
              <div className="text-2xl font-bold text-green-600">{results.successful_tests}</div>
              <div className="text-sm text-green-600">Successful</div>
            </div>
            <div className="bg-red-50 rounded-lg p-4 text-center">
              <div className="text-2xl font-bold text-red-600">{results.failed_tests}</div>
              <div className="text-sm text-red-600">Failed</div>
            </div>
            <div className="bg-blue-50 rounded-lg p-4 text-center">
              <div className="text-2xl font-bold text-blue-600">
                {results.evaluations ? results.evaluations.length : 0}
              </div>
              <div className="text-sm text-blue-600">Evaluations</div>
            </div>
          </div>

          {/* Test Results Details */}
          <div className="space-y-4">
            <h4 className="font-medium text-slate-100">Test Executions</h4>
            {results.test_results.map((execution) => (
              <div key={execution.id} className="border border-slate-700 rounded-lg p-4">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-2 mb-2">
                      {execution.customer_support_response.startsWith('Error:') ? (
                        <XCircle className="h-5 w-5 text-red-500" />
                      ) : (
                        <CheckCircle className="h-5 w-5 text-green-500" />
                      )}
                      <span className="text-sm font-medium text-slate-100">
                        Test Case ID: {execution.test_case_id}
                      </span>
                    </div>
                    
                    <p className="text-sm text-slate-300 mb-2">
                      <strong>Response:</strong> {execution.customer_support_response}
                    </p>
                    
                    <div className="flex items-center space-x-4 text-xs text-slate-400">
                      <span className="flex items-center">
                        <Clock className="h-3 w-3 mr-1" />
                        {execution.execution_time.toFixed(2)}s
                      </span>
                      {execution.tokens_used && (
                        <span className="flex items-center">
                          <Zap className="h-3 w-3 mr-1" />
                          {execution.tokens_used} tokens
                        </span>
                      )}
                      <span className="flex items-center">
                        <Database className="h-3 w-3 mr-1" />
                        Version ID: {execution.aut_version_id}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Evaluation Results */}
          {results.evaluations && results.evaluations.length > 0 && (
            <div className="mt-6 space-y-4">
              <h4 className="font-medium text-slate-100">Evaluation Results</h4>
              {results.evaluations.map((evaluation) => (
                <div key={evaluation.id} className="border border-slate-700 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-slate-100">
                      Test Execution ID: {evaluation.test_execution_id}
                    </span>
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(evaluation.overall_score)}`}>
                      Score: {evaluation.overall_score.toFixed(2)}
                    </span>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                    <div>
                      <span className="text-slate-300">Faithfulness:</span>
                      <span className="ml-2 font-medium">{evaluation.faithfulness_score?.toFixed(2) || 'N/A'}</span>
                    </div>
                    <div>
                      <span className="text-slate-300">Relevance:</span>
                      <span className="ml-2 font-medium">{evaluation.relevance_score?.toFixed(2) || 'N/A'}</span>
                    </div>
                    <div>
                      <span className="text-slate-300">Completeness:</span>
                      <span className="ml-2 font-medium">{evaluation.completeness_score?.toFixed(2) || 'N/A'}</span>
                    </div>
                  </div>
                  
                  {evaluation.judge_feedback && (
                    <div className="mt-2">
                      <span className="text-slate-300 text-sm">Feedback:</span>
                      <p className="text-sm text-slate-100 mt-1">{evaluation.judge_feedback}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default TestExecution;
